import React from 'react';
import { identity } from 'lodash';
import {
    getCombinedAudience,
    getCustomContextualSegment,
    getFirstPartyAudience,
    getPredictiveAudience,
    getMyAudience,
    getMRTAudience,
    getSRTAudience,
} from 'services/api/audiencesApi';
import { COMBINED_AUDIENCES_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/combined-audience/combinedAudiencesReportId';
import { CUSTOM_CONTEXTUAL_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/custom-contextual/customContextualReportId';
import { FIRST_PARTY_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/first-party/firstPartyReportId';
import { PREDICTIVE_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/lookalike/predictiveReportId';
import { PIXEL_BASED_REPORT_ID } from '../../campaigns/modules/campaigns-reports/config/reports/pixel-based/pixelBasedReportId';
import { addIndication, INDICATION_TYPES } from '../../taboola-common-frontend-modules/Indications';
import { FormattedMessage } from '../../taboola-common-frontend-modules/i18n';
import { fetchAudienceForEditError, fetchAudienceForEditSuccess, requestFetchAudienceForEdit } from '../actions';
import { AUDIENCE_TYPE, ENTITY } from '../config';
import {
    transformCombinedAudiencesSegmentFromGW,
    transformExternalAudiencesSegmentFromGW,
    transformPixelBasedAudienceFromGW,
} from '../transformers';

const indication = {
    message: (
        <FormattedMessage
            id="campaign.drawer.error"
            defaultMessage="Something went wrong when we tried to fetch the requested campaign."
        />
    ),
    type: INDICATION_TYPES.ERROR,
    highlight: <FormattedMessage id="error.highlight" />,
};

const apis = {
    [PIXEL_BASED_REPORT_ID]: getMyAudience,
    [PREDICTIVE_REPORT_ID]: getPredictiveAudience,
    [FIRST_PARTY_REPORT_ID]: getFirstPartyAudience,
    [CUSTOM_CONTEXTUAL_REPORT_ID]: getCustomContextualSegment,
    [COMBINED_AUDIENCES_REPORT_ID]: getCombinedAudience,
    [ENTITY.SEARCH_KEYWORDS]: getSRTAudience,
    [ENTITY.MAIL_DOMAINS]: getMRTAudience,
};

const transformers = {
    [PIXEL_BASED_REPORT_ID]: transformPixelBasedAudienceFromGW,
    [PREDICTIVE_REPORT_ID]: identity,
    [FIRST_PARTY_REPORT_ID]: identity,
    [CUSTOM_CONTEXTUAL_REPORT_ID]: identity,
    [COMBINED_AUDIENCES_REPORT_ID]: transformCombinedAudiencesSegmentFromGW,
    [AUDIENCE_TYPE.SEARCH_KEYWORDS]: transformExternalAudiencesSegmentFromGW,
    [AUDIENCE_TYPE.MAIL_DOMAINS]: transformExternalAudiencesSegmentFromGW,
};

const fetchAudienceForEdit =
    (accountId, audienceId, reportType, entity, { hideError } = {}) =>
    async dispatch => {
        dispatch(requestFetchAudienceForEdit());

        try {
            const audience = await getAudience(accountId, audienceId, reportType, entity);
            dispatch(fetchAudienceForEditSuccess(audience));
            return audience;
        } catch (error) {
            if (!hideError) {
                dispatch(addIndication(indication));
                dispatch(fetchAudienceForEditError(error));
            }
        }
    };

const getAudience = async (accountId, audienceId, reportType, entity) => {
    if (entity === ENTITY.SEARCH_KEYWORDS || entity === ENTITY.MAIL_DOMAINS) {
        return fetchAndTransformAudienceByType(accountId, audienceId, entity);
    }
    return fetchAndTransformAudienceByType(accountId, audienceId, reportType);
};

const fetchAndTransformAudienceByType = async (accountId, audienceId, type) => {
    const audience = await apis[type](accountId, audienceId);
    return transformers[type](audience);
};

export default fetchAudienceForEdit;
